'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
	UserIcon,
	BuildingOfficeIcon,
	KeyIcon,
	ShieldCheckIcon,
	BellIcon,
	Cog6ToothIcon,
	PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/button';
import { toast } from 'sonner';

const Profile = () => {
	const [loading, setLoading] = useState(false);
	const [keyResetLoading, setKeyResetLoading] = useState(false);
	const [activeTab, setActiveTab] = useState('profile');
	const [preferences, setPreferences] = useState(null);
	const [preferencesLoading, setPreferencesLoading] = useState(false);
	const [testNotificationLoading, setTestNotificationLoading] =
		useState(false);

	const { user, updateUser } = useAuth();
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
	} = useForm();

	useEffect(() => {
		if (user) {
			setValue('name', user.name);
			setValue('contactEmail', user.contactEmail);
			setValue('contactPhone', user.contactPhone || '');
			setValue('callbackUrl', user.callbackUrl);
		}
	}, [user, setValue]);

	useEffect(() => {
		if (activeTab === 'notifications') {
			fetchPreferences();
		}
	}, [activeTab]);

	const fetchPreferences = async () => {
		try {
			setPreferencesLoading(true);
			const response = await apiService.getNotificationPreferences();

			if (response.data.status === 200) {
				setPreferences(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching preferences:', error);
		} finally {
			setPreferencesLoading(false);
		}
	};

	const onSubmit = async (data) => {
		setLoading(true);
		try {
			const response = await apiService.updateInstitution(user.id, data);

			if (response.data.status === 200) {
				// Update user with the response data structure
				const updatedUser = response.data.data.institution || {
					...user,
					...data,
				};
				updateUser(updatedUser);
				toast.success('Profile updated successfully!');
			} else {
				throw new Error(
					response.data.message || 'Failed to update profile',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to update profile';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	const handleResetApiKey = async () => {
		if (
			!window.confirm(
				'Are you sure you want to reset your API key? This will invalidate your current key and you will need to update your applications.',
			)
		) {
			return;
		}

		setKeyResetLoading(true);
		try {
			const response = await apiService.resetTestKeys();

			if (response.data.status === 200) {
				// Update logic to handle environment-specific key resets properly
				const newTestApiKey = response.data.data.test.apiKey;
				updateUser({ ...user, apiKey: newTestApiKey });
				toast.success(
					'Test API key reset successfully! Please update your test environment with the new key.',
				);
			} else {
				throw new Error(
					response.data.message || 'Failed to reset test API key',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to reset API key';
			toast.error(message);
		} finally {
			setKeyResetLoading(false);
		}
	};

	const handleUpdatePreferences = async (newPreferences) => {
		try {
			const response = await apiService.updateNotificationPreferences(
				newPreferences,
			);

			if (response.data.status === 200) {
				setPreferences(newPreferences);
				toast.success('Notification preferences updated successfully!');
			} else {
				throw new Error(
					response.data.message || 'Failed to update preferences',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to update preferences';
			toast.error(message);
		}
	};

	const handleSendTestNotification = async () => {
		try {
			setTestNotificationLoading(true);
			const response = await apiService.sendTestNotification();

			if (response.data.status === 200) {
				toast.success('Test notification sent successfully!');
			} else {
				throw new Error(
					response.data.message || 'Failed to send test notification',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to send test notification';
			toast.error(message);
		} finally {
			setTestNotificationLoading(false);
		}
	};

	const tabs = [
		{ id: 'profile', name: 'Profile', icon: UserIcon },
		{ id: 'notifications', name: 'Notifications', icon: BellIcon },
	];

	if (!user) {
		return (
			<div className='flex items-center justify-center h-64'>
				<LoadingSpinner size='lg' />
			</div>
		);
	}

	return (
		<div className='space-y-6'>
			{/* Page description */}
			<div>
				<p className='text-sm text-gray-500'>
					Manage your institution profile and settings
				</p>
			</div>

			{/* Tabs */}
			<div className='border-b border-gray-200'>
				<nav className='-mb-px flex space-x-8'>
					{tabs.map((tab) => {
						const isActive = activeTab === tab.id;
						return (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`
                  group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors
                  ${
						isActive
							? 'border-blue-500 text-blue-600'
							: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
					}
                `}>
								<tab.icon
									className={`
                  -ml-0.5 mr-2 h-5 w-5
                  ${
						isActive
							? 'text-blue-500'
							: 'text-gray-400 group-hover:text-gray-500'
					}
                `}
								/>
								{tab.name}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Tab Content */}
			{activeTab === 'profile' && (
				<div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
					{/* Profile Form */}
					<div className='lg:col-span-2'>
						<form
							onSubmit={handleSubmit(onSubmit)}
							className='card space-y-6'>
							<div>
								<h2 className='text-lg font-medium text-gray-900 mb-4 flex items-center'>
									<BuildingOfficeIcon className='h-5 w-5 mr-2 text-blue-600' />
									Institution Information
								</h2>

								<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
									<div className='sm:col-span-2'>
										<label
											htmlFor='name'
											className='block text-sm font-medium text-gray-700'>
											Institution Name *
										</label>
										<input
											{...register('name', {
												required:
													'Institution name is required',
											})}
											type='text'
											className='input-field mt-1'
											placeholder='Enter institution name'
										/>
										{errors.name && (
											<p className='mt-1 text-sm text-red-600'>
												{errors.name.message}
											</p>
										)}
									</div>

									<div>
										<label
											htmlFor='contactEmail'
											className='block text-sm font-medium text-gray-700'>
											Contact Email *
										</label>
										<input
											{...register('contactEmail', {
												required: 'Email is required',
												pattern: {
													value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
													message:
														'Invalid email address',
												},
											})}
											type='email'
											className='input-field mt-1'
											placeholder='Enter contact email'
										/>
										{errors.contactEmail && (
											<p className='mt-1 text-sm text-red-600'>
												{errors.contactEmail.message}
											</p>
										)}
									</div>

									<div>
										<label
											htmlFor='contactPhone'
											className='block text-sm font-medium text-gray-700'>
											Contact Phone
										</label>
										<input
											{...register('contactPhone')}
											type='tel'
											className='input-field mt-1'
											placeholder='Enter contact phone (optional)'
										/>
									</div>

									<div className='sm:col-span-2'>
										<label
											htmlFor='callbackUrl'
											className='block text-sm font-medium text-gray-700'>
											Callback URL *
										</label>
										<input
											{...register('callbackUrl', {
												required:
													'Callback URL is required',
											})}
											type='url'
											className='input-field mt-1'
											placeholder='https://your-domain.com/callback'
										/>
										{errors.callbackUrl && (
											<p className='mt-1 text-sm text-red-600'>
												{errors.callbackUrl.message}
											</p>
										)}
									</div>
								</div>
							</div>

							<div className='flex items-center justify-end pt-6 border-t border-gray-200'>
								<Button
									type='primary'
									label={
										loading
											? 'Updating...'
											: 'Update Profile'
									}
									onClick={handleSubmit(onSubmit)}
									loading={loading}
									disabled={loading}
								/>
							</div>
						</form>
					</div>

					{/* Sidebar */}
					<div className='space-y-6'>
						{/* API Credentials */}
						<div className='card'>
							<h3 className='text-lg font-medium text-gray-900 mb-4 flex items-center'>
								<KeyIcon className='h-5 w-5 mr-2 text-blue-600' />
								API Credentials
							</h3>

							<div className='space-y-4'>
								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
										Client ID
									</label>
									<div className='flex items-center space-x-2'>
										<code className='flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all'>
											{user.clientId}
										</code>
										<Button
											type='secondary'
											variant='outlined'
											label='Copy'
											size='S'
											onClick={() =>
												navigator.clipboard.writeText(
													user.clientId,
												)
											}
										/>
									</div>
								</div>

								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
Test API Key - kai_test
									</label>
									<div className='flex items-center space-x-2'>
										<code className='flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono'>
											{'•'.repeat(32)}
										</code>
										<Button
											type='danger'
											variant='outlined'
											label='Reset'
											size='S'
											onClick={handleResetApiKey}
											loading={keyResetLoading}
											disabled={keyResetLoading}
										/>
									</div>
									<p className='text-xs text-gray-500 mt-1'>
										Keep your API key secure. Reset if
										compromised.
									</p>
								</div>
							</div>
						</div>

						{/* Account Info */}
						<div className='card'>
							<h3 className='text-lg font-medium text-gray-900 mb-4 flex items-center'>
								<UserIcon className='h-5 w-5 mr-2 text-blue-600' />
								Account Information
							</h3>

							<div className='space-y-3'>
								<div className='flex justify-between'>
									<span className='text-sm text-gray-500'>
										Account ID
									</span>
									<span className='text-sm font-medium text-gray-900'>
										{user.id}
									</span>
								</div>

								<div className='flex justify-between'>
									<span className='text-sm text-gray-500'>
										Created
									</span>
									<span className='text-sm font-medium text-gray-900'>
										{user.createdAt
											? new Date(
													user.createdAt,
											  ).toLocaleDateString()
											: 'N/A'}
									</span>
								</div>

								<div className='flex justify-between'>
									<span className='text-sm text-gray-500'>
										Status
									</span>
									<span className='status-badge status-paid'>
										Active
									</span>
								</div>
							</div>
						</div>

						{/* Security Notice */}
						<div className='card bg-blue-50 border-blue-200'>
							<div className='flex'>
								<div className='flex-shrink-0'>
									<ShieldCheckIcon className='h-5 w-5 text-blue-400' />
								</div>
								<div className='ml-3'>
									<h3 className='text-sm font-medium text-blue-800'>
										Security Best Practices
									</h3>
									<div className='mt-2 text-sm text-blue-700'>
										<ul className='list-disc pl-5 space-y-1'>
											<li>
												Never share your API credentials
											</li>
											<li>
												Use HTTPS for all API requests
											</li>
											<li>
												Regularly monitor your API usage
											</li>
											<li>Reset keys if compromised</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{activeTab === 'notifications' && (
				<div className='space-y-6'>
					<div className='flex items-center justify-between'>
						<h2 className='text-lg font-medium text-gray-900 flex items-center'>
							<Cog6ToothIcon className='h-5 w-5 mr-2 text-blue-600' />
							Notification Settings
						</h2>
						<Button
							type='secondary'
							variant='outlined'
							label='Send Test'
							iconLeft={<PaperAirplaneIcon className='h-4 w-4' />}
							onClick={handleSendTestNotification}
							loading={testNotificationLoading}
							disabled={testNotificationLoading}
						/>
					</div>

					{preferencesLoading ? (
						<div className='flex items-center justify-center h-32'>
							<LoadingSpinner size='lg' />
						</div>
					) : (
						<div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
							{/* Email Preferences */}
							<div className='card'>
								<h3 className='text-md font-medium text-gray-900 mb-4'>
									Email Notifications
								</h3>

								<div className='space-y-4'>
									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900'>
												Debt Reminders
											</p>
											<p className='text-xs text-gray-500'>
												Get notified about upcoming due
												dates
											</p>
										</div>
										<input
											type='checkbox'
											checked={
												preferences?.email
													?.debtReminders || false
											}
											onChange={(e) =>
												handleUpdatePreferences({
													...preferences,
													email: {
														...preferences?.email,
														debtReminders:
															e.target.checked,
													},
												})
											}
											className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
										/>
									</div>

									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900'>
												Payment Confirmations
											</p>
											<p className='text-xs text-gray-500'>
												Receive payment confirmation
												emails
											</p>
										</div>
										<input
											type='checkbox'
											checked={
												preferences?.email
													?.paymentConfirmations ||
												false
											}
											onChange={(e) =>
												handleUpdatePreferences({
													...preferences,
													email: {
														...preferences?.email,
														paymentConfirmations:
															e.target.checked,
													},
												})
											}
											className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
										/>
									</div>

									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900'>
												System Alerts
											</p>
											<p className='text-xs text-gray-500'>
												Important system notifications
											</p>
										</div>
										<input
											type='checkbox'
											checked={
												preferences?.email
													?.systemAlerts || false
											}
											onChange={(e) =>
												handleUpdatePreferences({
													...preferences,
													email: {
														...preferences?.email,
														systemAlerts:
															e.target.checked,
													},
												})
											}
											className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
										/>
									</div>
								</div>
							</div>

							{/* SMS Preferences */}
							<div className='card'>
								<h3 className='text-md font-medium text-gray-900 mb-4'>
									SMS Notifications
								</h3>

								<div className='space-y-4'>
									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900'>
												Urgent Reminders
											</p>
											<p className='text-xs text-gray-500'>
												SMS for overdue payments
											</p>
										</div>
										<input
											type='checkbox'
											checked={
												preferences?.sms
													?.urgentReminders || false
											}
											onChange={(e) =>
												handleUpdatePreferences({
													...preferences,
													sms: {
														...preferences?.sms,
														urgentReminders:
															e.target.checked,
													},
												})
											}
											className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
										/>
									</div>

									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900'>
												Payment Alerts
											</p>
											<p className='text-xs text-gray-500'>
												SMS for successful payments
											</p>
										</div>
										<input
											type='checkbox'
											checked={
												preferences?.sms
													?.paymentAlerts || false
											}
											onChange={(e) =>
												handleUpdatePreferences({
													...preferences,
													sms: {
														...preferences?.sms,
														paymentAlerts:
															e.target.checked,
													},
												})
											}
											className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
										/>
									</div>

									<div className='flex items-center justify-between'>
										<div>
											<p className='text-sm font-medium text-gray-900'>
												Security Alerts
											</p>
											<p className='text-xs text-gray-500'>
												SMS for security-related events
											</p>
										</div>
										<input
											type='checkbox'
											checked={
												preferences?.sms
													?.securityAlerts || false
											}
											onChange={(e) =>
												handleUpdatePreferences({
													...preferences,
													sms: {
														...preferences?.sms,
														securityAlerts:
															e.target.checked,
													},
												})
											}
											className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
										/>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Profile;
